/**
 * Autenticação gov.br do SNCR numa janela à parte.
 *
 * A Anvisa só devolve o `session_id` para um `client_url` em host `.br`, por isso a troca por token
 * acontece na página-ponte estática (`public/sncr/callback.html`, publicada no host `.br`), que
 * devolve o `access_token` para esta aba via `postMessage`. Aqui ficam só a abertura da janela e a
 * escuta da mensagem; o token é entregue a quem chamou e não é guardado em lugar nenhum.
 */

export const SNCR_MENSAGEM_TIPO = 'sncr-token'

/** Mensagens explícitas para o chamador distinguir desistência de falha. */
export const SNCR_POPUP_BLOQUEADA = 'SNCR_POPUP_BLOQUEADA'
export const SNCR_POPUP_FECHADA = 'SNCR_POPUP_FECHADA'
export const SNCR_ESPERA_EXPIROU = 'SNCR_ESPERA_EXPIROU'

/** Enquanto a janela estiver aberta, espera-se até isto pelo retorno do gov.br. */
export const SNCR_ESPERA_MS = 10 * 60 * 1000
const INTERVALO_POPUP_FECHADA_MS = 500

export type MensagemPonte = {
  type: typeof SNCR_MENSAGEM_TIPO
  token?: string
  erro?: string
}

/** URL da página-ponte no host `.br`; em desenvolvimento cai no próprio dev server (localhost é aceito pela Anvisa). */
export const urlPonteSncr = (): string =>
  process.env.REACT_APP_SNCR_PONTE_URL || `${window.location.origin}/sncr/callback.html`

export const origemPonteSncr = (): string => new URL(urlPonteSncr()).origin

export const montarUrlLoginGovbr = (urlLogin: string): string => {
  const url = new URL(urlLogin)
  url.searchParams.set('client_url', urlPonteSncr())
  return url.toString()
}

/**
 * Abre a janela do login gov.br. Precisa ser chamado de forma síncrona dentro do clique (bloqueador
 * de popup) e sem `noopener`: a ponte depende de `window.opener` para devolver o token.
 */
export const abrirLoginGovbr = (urlLogin: string): Window | null =>
  window.open(
    montarUrlLoginGovbr(urlLogin),
    'sncr-govbr',
    'popup=yes,width=520,height=720,resizable=yes,scrollbars=yes'
  )

/**
 * Valida uma mensagem recebida pela aba. Aceita só o que vem da origem da ponte, da janela que
 * abrimos, com o tipo esperado. Igualdade exata de origem, nunca `includes`.
 */
export const validarMensagemPonte = (
  event: Pick<MessageEvent, 'origin' | 'source' | 'data'>,
  popup: Window | null,
  origemEsperada: string = origemPonteSncr()
): MensagemPonte | undefined => {
  if (event.origin !== origemEsperada) return undefined
  if (!popup || event.source !== popup) return undefined
  const data = event.data as Partial<MensagemPonte> | null | undefined
  if (!data || typeof data !== 'object' || data.type !== SNCR_MENSAGEM_TIPO) return undefined
  if (typeof data.token !== 'string' && typeof data.erro !== 'string') return undefined
  return data as MensagemPonte
}

export type EsperaToken = {
  promise: Promise<string>
  /** Cancela a espera (ex.: modal fechada): o listener sai e a janela, se aberta, é fechada. */
  cancelar: () => void
}

/**
 * Espera a primeira mensagem válida da ponte e resolve com o token. Rejeita com
 * {@link SNCR_POPUP_FECHADA} se o médico fechar a janela antes, {@link SNCR_ESPERA_EXPIROU} após
 * {@link SNCR_ESPERA_MS}, ou com a mensagem de erro que a própria ponte reportou.
 */
export const aguardarTokenGovbr = (
  popup: Window,
  opcoes: { timeoutMs?: number; origemEsperada?: string } = {}
): EsperaToken => {
  const timeoutMs = opcoes.timeoutMs ?? SNCR_ESPERA_MS
  const origemEsperada = opcoes.origemEsperada ?? origemPonteSncr()

  let limpar: () => void = () => {}
  const promise = new Promise<string>((resolve, reject) => {
    let encerrado = false

    const encerrar = () => {
      if (encerrado) return
      encerrado = true
      window.removeEventListener('message', onMessage)
      window.clearInterval(intervalo)
      window.clearTimeout(timeout)
    }

    const onMessage = (event: MessageEvent) => {
      const mensagem = validarMensagemPonte(event, popup, origemEsperada)
      if (!mensagem) return
      encerrar()
      if (typeof mensagem.token === 'string' && mensagem.token.length > 0) {
        resolve(mensagem.token)
      } else {
        reject(new Error(mensagem.erro || 'A autenticação gov.br não devolveu o token.'))
      }
    }

    const intervalo = window.setInterval(() => {
      if (popup.closed) {
        encerrar()
        reject(new Error(SNCR_POPUP_FECHADA))
      }
    }, INTERVALO_POPUP_FECHADA_MS)

    const timeout = window.setTimeout(() => {
      encerrar()
      reject(new Error(SNCR_ESPERA_EXPIROU))
    }, timeoutMs)

    window.addEventListener('message', onMessage)

    limpar = () => {
      encerrar()
      if (!popup.closed) popup.close()
      reject(new Error(SNCR_POPUP_FECHADA))
    }
  })

  return { promise, cancelar: () => limpar() }
}
